(() => {
  if (customElements.get('ct-delivery-estimate')) return;

  class CtDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.hoverTrigger = this.querySelector(
        '[data-ct-delivery-hover-trigger]'
      );
      this.panel = this.querySelector(
        '[data-ct-delivery-panel]'
      );
      this.closeTimer = null;

      this.renderDates();
      this.bindEvents();
    }

    bindEvents() {
      if (!this.hoverTrigger || !this.panel) return;

      this.hoverTrigger.addEventListener('mouseenter', () => {
        if (!this.isHoverCapable()) return;
        this.cancelScheduledClose();
        this.open();
      });

      this.hoverTrigger.addEventListener('mouseleave', () => {
        if (!this.isHoverCapable()) return;
        this.scheduleClose();
      });

      this.panel.addEventListener('mouseenter', () => {
        if (!this.isHoverCapable()) return;
        this.cancelScheduledClose();
      });

      this.panel.addEventListener('mouseleave', () => {
        if (!this.isHoverCapable()) return;
        this.scheduleClose();
      });

      this.hoverTrigger.addEventListener('focus', () => {
        this.cancelScheduledClose();
        this.open();
      });

      this.hoverTrigger.addEventListener('blur', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.scheduleClose(0);
      });

      this.hoverTrigger.addEventListener('click', () => {
        if (this.isHoverCapable()) return;
        this.toggle();
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.close();
        this.hoverTrigger.focus();
      });
    }

    isHoverCapable() {
      return window.matchMedia(
        '(hover: hover) and (pointer: fine)'
      ).matches;
    }

    open() {
      this.cancelScheduledClose();
      this.panel.hidden = false;
      this.hoverTrigger.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
    }

    close() {
      this.cancelScheduledClose();
      this.panel.hidden = true;
      this.hoverTrigger.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
    }

    toggle() {
      const isOpen =
        this.hoverTrigger.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    scheduleClose(delay = 160) {
      this.cancelScheduledClose();
      this.closeTimer = window.setTimeout(() => {
        this.close();
      }, delay);
    }

    cancelScheduledClose() {
      if (!this.closeTimer) return;
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    renderDates() {
      const today = this.startOfToday();
      const shipMin = this.addDays(
        today,
        this.numberFromData('shipMinDays', 3)
      );
      const shipMax = this.addDays(
        today,
        this.numberFromData('shipMaxDays', 5)
      );
      const deliveryMin = this.addDays(
        today,
        this.numberFromData('deliveryMinDays', 10)
      );
      const deliveryMax = this.addDays(
        today,
        this.numberFromData('deliveryMaxDays', 16)
      );

      const orderDateText = this.formatSingleDate(today);
      const shipRangeText = this.formatDateRange(shipMin, shipMax);
      const deliveryRangeText = this.formatDateRange(
        deliveryMin,
        deliveryMax
      );

      this.setText('[data-ct-order-date]', orderDateText);
      this.setText('[data-ct-ship-range]', shipRangeText);
      this.setText(
        '[data-ct-delivery-range]',
        deliveryRangeText
      );
      this.setText(
        '[data-ct-delivery-range-panel]',
        deliveryRangeText
      );
    }

    numberFromData(key, fallback) {
      const value = Number.parseInt(this.dataset[key], 10);
      return Number.isFinite(value) ? value : fallback;
    }

    startOfToday() {
      const now = new Date();
      return new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );
    }

    addDays(date, numberOfDays) {
      const result = new Date(date);
      result.setDate(result.getDate() + numberOfDays);
      return result;
    }

    get locale() {
      return (
        document.documentElement.lang ||
        navigator.language ||
        'en-US'
      );
    }

    formatSingleDate(date) {
      return new Intl.DateTimeFormat(this.locale, {
        month: 'short',
        day: 'numeric'
      }).format(date);
    }

    formatDateRange(start, end) {
      const formatter = new Intl.DateTimeFormat(this.locale, {
        month: 'short',
        day: 'numeric'
      });

      if (typeof formatter.formatRange === 'function') {
        return formatter.formatRange(start, end);
      }

      const sameYear =
        start.getFullYear() === end.getFullYear();
      const sameMonth =
        sameYear && start.getMonth() === end.getMonth();

      if (sameMonth) {
        const month = new Intl.DateTimeFormat(this.locale, {
          month: 'short'
        }).format(start);

        return `${month} ${start.getDate()} – ${end.getDate()}`;
      }

      return `${formatter.format(start)} – ${formatter.format(end)}`;
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((element) => {
        element.textContent = value;
      });
    }
  }

  customElements.define(
    'ct-delivery-estimate',
    CtDeliveryEstimate
  );
})();
